import { ElementType } from 'react';
import { Icon, RadioCard, RadioCardRootProps } from '@chakra-ui/react';

import { useIconRepository } from '~/core/shared/contexts';
import {
  FormFieldControlledComponent,
  FormFieldControlledComponentProps,
} from '../../types/fields';
import { Option } from '../../types/utility';

type RadioButtonGroupProps = RadioCardRootProps & {
  options: Option<string, { icon?: ElementType | string }>[];
};

export const RadioButtonGroup: FormFieldControlledComponent<
  string,
  RadioButtonGroupProps
> = ({
  _ref,
  invalid,
  name,
  options,
  ...props
}: FormFieldControlledComponentProps<string, RadioButtonGroupProps>) => {
  const iconRepository = useIconRepository();

  // FormLabel renders this field's label with the id `${name}-label`, so the
  // card group is named by it unless a caller passes its own `aria-labelledby`.
  return (
    <RadioCard.Root
      ref={_ref}
      aria-labelledby={`${name}-label`}
      name={name}
      {...props}
    >
      {options.map((option, idx) => {
        const icon =
          typeof option.meta?.icon === 'string'
            ? iconRepository[option.meta.icon] || undefined
            : option.meta?.icon;
        return (
          <RadioCard.Item
            key={option.value}
            id={`${name}-${idx}`}
            invalid={invalid}
            value={option.value}
          >
            <RadioCard.ItemHiddenInput />
            <RadioCard.ItemControl>
              {icon && <Icon aria-hidden as={icon} />}
              <RadioCard.ItemIndicator />
              <RadioCard.ItemText>{option.label}</RadioCard.ItemText>
            </RadioCard.ItemControl>
          </RadioCard.Item>
        );
      })}
    </RadioCard.Root>
  );
};

RadioButtonGroup.controlled = true;
RadioButtonGroup.displayName = 'RadioButtonGroup';
